import { Accordion, AccordionDetails, AccordionSummary, Backdrop, Box, Button, Checkbox, Chip, Divider, Fade, Grid, Modal, Pagination, Paper, Skeleton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faBullseye, faCalendarAlt, faCheckCircle, faChevronDown, faClock, faEuroSign, faExclamationTriangle, faFileAlt, faFolderOpen, faGavel, faGlobe, faGraduationCap, faHourglassHalf, faListAlt, faMapMarkerAlt, faPhoneAlt, faTags, faTimes, faUserPlus, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { useLang } from '../../../../language';
import { InputText } from '../../../../components/Inputs/InputText';

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 640,
    maxWidth: '94%',
    maxHeight: '88vh',
    overflowY: 'auto' as 'auto',
    overflowX: 'hidden' as 'hidden',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
}

const modalStyleActividad = {
    ...modalStyle,
    width: 1100,
}

/**
 * Fila de dato dins del modal d'activitat (icono + etiqueta + valor).
 */
const FilaDato = ({ icono, label, valor, multiline }: any) => {

    if (!valor) return null

    return (
        <Typography variant='body2' className='my-0' sx={multiline ? { whiteSpace: 'pre-line' } : undefined}>
            <FontAwesomeIcon icon={icono} style={{ marginRight: 6, fontSize: 12 }} />
            <span className='color-primary fw-500'>{label}: </span>
            {valor}
        </Typography>
    )
}

/**
 * Modal de detall d'una activitat del WS d'Activitats
 * (accion=obtener_detalle_actividad&id=N).
 */
export const ModalActividad = ({ actividad, detalle, inscritos, cargando, onClose, personas = [], personasCargando = false, personasError, personasPaginacion = { page: 1, last_page: 1 }, onPersonasPageChange, onPersonasBuscar, checkedPersonas = new Set(), onTogglePersona, personasMarcadas = [], onInscribir, inscribiendo = false, onAnularInscripcion, anulando = false }: any) => {

    const Lang = useLang()

    const [busquedaPersona, setBusquedaPersona] = useState('')

    // Al cambiar de actividad se resetea la búsqueda local del selector
    useEffect(() => {

        setBusquedaPersona('')

    }, [actividad?.id])

    const handleBuscarPersonas = (e: any) => {

        e.preventDefault()

        onPersonasBuscar?.(busquedaPersona)
    }

    const handleLimpiarPersonas = () => {

        setBusquedaPersona('')

        onPersonasBuscar?.('')
    }

    // El contenido del modal procede SOLO de la respuesta de
    // `obtener_detalle_actividad`. La fila del listado (`actividad`) únicamente
    // actúa de disparador (id) mientras carga el detalle.
    const actividadActual = detalle || {}

    const actividadInscritos = inscritos || []

    // Personas inscritas de forma ACTIVA: se ignoran las anuladas (in_estado '109')
    // para que puedan volver a seleccionarse. Se comparan por NIF (clave común con el WS).
    const normalizaNif = (nif: any) => String(nif || '').trim().toUpperCase()

    const nifsInscritos = new Set(
        actividadInscritos
            .filter((ins: any) => String(ins.in_estado) !== '109')
            .map((ins: any) => normalizaNif(ins.nif))
    )

    const esInscrito = (p: any) => nifsInscritos.has(normalizaNif(p.nif))

    // Seleccionadas que aún no están inscritas (las que realmente se enviarán al WS)
    const nuevasMarcadas = personasMarcadas.filter((p: any) => !esInscrito(p))

    const lugarCompleto = [
        actividadActual.lugar,
        actividadActual.direccion,
        [actividadActual.cp, actividadActual.poblacion].filter(Boolean).join(' '),
        actividadActual.espacio,
    ].filter(Boolean).join('\n')

    const seccionesActividad = [
        { id: 'resumen', label: Lang('ACTIVITAT_RESUMEN'), icon: faFileAlt, texto: actividadActual.resumen },
        { id: 'objetivos', label: Lang('ACTIVITAT_OBJETIVOS'), icon: faBullseye, texto: actividadActual.objetivos },
        { id: 'organizador', label: Lang('ACTIVITAT_ORGANIZADOR'), icon: faBuilding, texto: actividadActual.organizador },
        { id: 'destinatarios', label: Lang('ACTIVITAT_DESTINATARIOS'), icon: faUsers, texto: actividadActual.destinatarios },
        { id: 'programa', label: Lang('ACTIVITAT_PROGRAMA'), icon: faListAlt, texto: actividadActual.programa },
        { id: 'profesorado', label: Lang('ACTIVITAT_PROFESORADO'), icon: faGraduationCap, texto: actividadActual.profesorado },
        { id: 'derechos', label: Lang('ACTIVITAT_DERECHO_INSCRIPCION'), icon: faGavel, texto: actividadActual.derechosinsc },
        { id: 'documentacion', label: Lang('ACTIVITAT_DOCUMENTACION'), icon: faFolderOpen, texto: actividadActual.documentacion },
        { id: 'observaciones', label: Lang('ACTIVITAT_OBSERVACIONES'), icon: faExclamationTriangle, texto: actividadActual.observaciones },
    ].filter((seccion: any) => seccion.texto)

    return (
        <Modal
            open={!!actividad}
            onClose={onClose}
            aria-labelledby="modal-actividad"
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 200,
            }}
        >
            <Fade in={!!actividad}>

                <Paper sx={modalStyleActividad}>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>

                        <Typography variant='h6' className='fw-500 my-0'>
                            {Lang('ACTIVITAT_DETALLE')}
                        </Typography>

                        <Button
                            size="small"
                            color="inherit"
                            onClick={onClose}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </Button>
                    </Box>

                    {
                        actividad &&
                        <Box className='p-3'>
                            {
                                cargando &&
                                <Box>
                                    <Skeleton variant="text" animation="wave" width="70%" sx={{ mb: 1 }} />
                                    <Skeleton variant="text" animation="wave" width="40%" sx={{ mb: 2 }} />
                                    <Skeleton variant="rectangular" animation="wave" sx={{ borderRadius: 1.5, mb: 2, height: 90 }} />
                                    <Skeleton variant="text" animation="wave" />
                                    <Skeleton variant="text" animation="wave" />
                                    <Skeleton variant="text" animation="wave" width="60%" />
                                </Box>
                            }

                            {
                                !cargando && !actividadActual?.id &&
                                <Box className='p-3'>
                                    <Typography variant='body1' className='my-0' sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                                        {Lang('SIN_RESULTADOS')}
                                    </Typography>
                                </Box>
                            }

                            {
                                !cargando && actividadActual?.id &&
                                <Box>
                                    {/* Capçalera: estat + títol */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        {
                                            (actividadActual.estado || actividadActual.tipo) &&
                                            <Chip
                                                label={actividadActual.estado || actividadActual.tipo}
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        <Typography variant='h6' className='fw-500 my-0' sx={{ wordBreak: 'break-word', minWidth: 0 }}>
                                            {actividadActual.titulo}
                                        </Typography>
                                    </Box>

                                    {/* Dades clau */}
                                    <Paper variant="outlined" className='p-3 mb-3' sx={{ borderRadius: 2, bgcolor: 'action.hover' }}>

                                        <Box sx={{ display: 'grid', gap: 0.75 }}>
                                            <FilaDato icono={faTags} label={Lang('ACTIVITAT_TIPO')} valor={actividadActual.tipoTxt || actividadActual.tipo} />
                                            <FilaDato icono={faCalendarAlt} label={Lang('ACTIVITAT_FECHAS')} valor={actividadActual.fecha || [actividadActual.finicio, actividadActual.ffin].filter(Boolean).join(' / ')} />
                                            <FilaDato icono={faClock} label={Lang('ACTIVITAT_HORARIO')} valor={actividadActual.horario} />
                                            <FilaDato icono={faHourglassHalf} label={Lang('ACTIVITAT_DURACION')} valor={actividadActual.duracion} />
                                            <FilaDato icono={faMapMarkerAlt} label={Lang('ACTIVITAT_LUGAR')} valor={lugarCompleto} multiline />
                                            <FilaDato icono={faGlobe} label={Lang('WEB')} valor={actividadActual.web} />
                                            <FilaDato icono={faPhoneAlt} label={Lang('TELEFONO_CONTACTO')} valor={actividadActual.telefono} />
                                            <FilaDato icono={faUsers} label={Lang('EMAIL')} valor={actividadActual.mail} />
                                        </Box>

                                    </Paper>

                                    {/* 2 columnes: disponibilitat + contingut */}
                                    <Grid container spacing={2}>

                                        <Grid item xs={12} lg={5}>
                                            <Paper variant="outlined" className='p-3 h-100' sx={{ borderRadius: 2 }}>

                                                <Typography variant='subtitle2' className='fw-500 my-0 mb-2 color-primary'>
                                                    <FontAwesomeIcon icon={faEuroSign} style={{ marginRight: 6, fontSize: 14 }} />
                                                    {Lang('ACTIVITAT_DISPONIBILIDAD')}
                                                </Typography>

                                                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, bgcolor: 'action.selected', textAlign: 'center' }}>
                                                        <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary', display: 'block' }}>
                                                            {Lang('ACTIVITAT_PLAZAS_TOTALES')}
                                                        </Typography>
                                                        <Typography variant='h5' className='fw-500 my-0'>
                                                            {actividadActual.plazasTotales || '—'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, bgcolor: 'action.selected', textAlign: 'center' }}>
                                                        <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary', display: 'block' }}>
                                                            {Lang('ACTIVITAT_PLAZAS_DISPONIBLES')}
                                                        </Typography>
                                                        <Typography variant='h5' className='fw-500 my-0' sx={{ color: actividadActual.plazasDisponibles > 0 ? 'success.main' : 'error.main' }}>
                                                            {actividadActual.plazasDisponibles ?? 0}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {
                                                    actividadActual.tarifas?.length > 0 &&
                                                    <Box className='mb-2'>
                                                        {
                                                            actividadActual.tarifas.map((tarifa: any, indice: number) => (
                                                                <Box key={indice} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                    <Typography variant='body2' className='my-0' sx={{ color: 'text.secondary' }}>
                                                                        {tarifa.label}
                                                                    </Typography>
                                                                    <Typography variant='body2' className='fw-500 my-0'>
                                                                        {tarifa.monto}
                                                                    </Typography>
                                                                </Box>
                                                            ))
                                                        }
                                                    </Box>
                                                }

                                                {
                                                    actividadActual.ivaInfo &&
                                                    <Typography variant='caption' className='my-0 mb-2' sx={{ color: 'text.secondary', display: 'block', fontStyle: 'italic' }}>
                                                        * {actividadActual.ivaInfo}
                                                    </Typography>
                                                }

                                                {
                                                    actividadActual.fechaLimitePago &&
                                                    <Box className='mb-2' sx={{ bgcolor: 'warning.light', borderRadius: 1.5, px: 1.5, py: 1 }}>
                                                        <Typography variant='caption' className='my-0' sx={{ color: 'warning.dark' }}>
                                                            <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 6, fontSize: 12 }} />
                                                            {Lang('ACTIVITAT_FECHA_LIMITE_PAGO')}: <strong>{actividadActual.fechaLimitePago}</strong>
                                                        </Typography>
                                                    </Box>
                                                }

                                                <Divider className='my-2' />

                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary', display: 'block' }}>
                                                            {Lang('ACTIVITAT_COSTES_INSCRIPCION')}
                                                        </Typography>
                                                        <Typography variant='h5' className='fw-500 my-0 color-primary'>
                                                            {actividadActual.tarifas?.[0]?.monto || '—'}
                                                        </Typography>
                                                    </Box>

                                                    {
                                                        actividadActual.enlace &&
                                                        <Button component="a" href={actividadActual.enlace} target="_blank" rel="noreferrer" size="small" color="primary" variant="contained">
                                                            {Lang('MES_INFORMACIO')}
                                                        </Button>
                                                    }
                                                </Box>

                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} lg={7}>
                                            {
                                                seccionesActividad.length > 0 &&
                                                <Box sx={{ display: 'grid', gap: 1, mb: 2 }}>
                                                    {
                                                        seccionesActividad.map((seccion: any, indice: number) => (
                                                            <Accordion
                                                                key={seccion.id}
                                                                defaultExpanded={indice === 0}
                                                                disableGutters
                                                                sx={{ borderRadius: 1.5, '&:before': { display: 'none' }, boxShadow: 1 }}
                                                            >
                                                                <AccordionSummary
                                                                    expandIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                                                                    sx={{ '& .MuiAccordionSummary-content': { my: 0.5 } }}
                                                                >
                                                                    <Typography variant='subtitle2' className='fw-500 my-0'>
                                                                        <FontAwesomeIcon icon={seccion.icon} className='color-primary' style={{ marginRight: 8, fontSize: 13 }} />
                                                                        {seccion.label}
                                                                    </Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ pt: 0 }}>
                                                                    <Typography
                                                                        variant='body2'
                                                                        sx={{ color: 'text.secondary' }}
                                                                        dangerouslySetInnerHTML={{ __html: seccion.texto }}
                                                                    />
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        ))
                                                    }
                                                </Box>
                                            }

                                            {/* Inscrits */}
                                            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                                                <Box className='px-2 py-1' sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                                    <Typography variant='subtitle2' className='fw-500 my-1'>
                                                        <FontAwesomeIcon icon={faUsers} className='color-primary' style={{ marginRight: 8, fontSize: 13 }} />
                                                        {Lang('ACTIVITAT_INSCRITOS')} ({actividadInscritos.length})
                                                    </Typography>
                                                </Box>

                                                {
                                                    actividadInscritos.length === 0 ?
                                                    <Typography variant='body2' className='my-0' sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
                                                        {Lang('ACTIVITAT_SIN_INSCRITOS')}
                                                    </Typography>
                                                    :
                                                    <Box>
                                                        {
                                                            actividadInscritos.map((ins: any) => (
                                                                <Box
                                                                    key={ins.id}
                                                                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' } }}
                                                                >
                                                                    <Box sx={{ minWidth: 0, pr: 1 }}>
                                                                        <Typography variant='body2' className='fw-500 my-0' sx={{ wordBreak: 'break-word' }}>
                                                                            {ins.nombre}
                                                                        </Typography>
                                                                        <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary' }}>
                                                                            {ins.nif}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                                                        {
                                                                            ins.precio &&
                                                                            <Typography variant='body2' className='my-0 fw-500 color-primary'>
                                                                                {ins.precio} €
                                                                            </Typography>
                                                                        }
                                                                        {
                                                                            ins.estado &&
                                                                            <Chip label={ins.estado} size="small" variant="outlined" />
                                                                        }
                                                                        {
                                                                            ins.in_estado !== '109' &&
                                                                            <Button
                                                                                size="small"
                                                                                color="error"
                                                                                variant="outlined"
                                                                                disabled={anulando}
                                                                                onClick={() => onAnularInscripcion?.(ins)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faTimes} style={{ marginRight: 6, fontSize: 11 }} />
                                                                                {Lang('ANULAR_INSCRIPCION')}
                                                                            </Button>
                                                                        }
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        }
                                                    </Box>
                                                }
                                            </Paper>
                                        </Grid>

                                    </Grid>

                                    {/* Selecció de persones per inscriure a l'activitat */}
                                    <Paper variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>

                                        <Box className='px-2 py-1' sx={{ borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                            <Typography variant='subtitle2' className='fw-500 my-1'>
                                                <FontAwesomeIcon icon={faUserPlus} className='color-primary' style={{ marginRight: 8, fontSize: 13 }} />
                                                {Lang('INSCRIPCION_PERSONAS')}
                                            </Typography>
                                        </Box>

                                        <Box className='p-2'>

                                            <Box
                                                component="form"
                                                onSubmit={handleBuscarPersonas}
                                                sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'flex-end' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1.5 }}
                                            >
                                                <Box sx={{ flex: '1 1 220px' }}>
                                                    <InputText
                                                        label={Lang('BUSCAR_PERSONA')}
                                                        name='buscarPersona'
                                                        value={busquedaPersona}
                                                        onChange={(e: any) => setBusquedaPersona(e.target.value)}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button type="submit" size="small" color="primary" variant="contained">
                                                        {Lang('REALIZAR_BUSQUEDA')}
                                                    </Button>
                                                    <Button type="button" size="small" color="primary" variant="outlined" onClick={handleLimpiarPersonas}>
                                                        {Lang('LIMPIAR')}
                                                    </Button>
                                                </Box>
                                            </Box>

                                            {
                                                personasError &&
                                                <Typography variant='caption' className='my-0 mb-1' sx={{ color: 'error.main', display: 'block', textAlign: 'center' }}>
                                                    {String(personasError?.error || personasError?.message || personasError)}
                                                </Typography>
                                            }

                                            {
                                                personasCargando &&
                                                <Box sx={{ display: 'grid', gap: 0.75 }}>
                                                    {
                                                        [0, 1, 2, 3].map((indice: number) => (
                                                            <Box key={indice} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Skeleton variant="rounded" width={24} height={24} />
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Skeleton variant="text" sx={{ fontSize: '0.9rem' }} />
                                                                    <Skeleton variant="text" width="30%" sx={{ fontSize: '0.75rem' }} />
                                                                </Box>
                                                            </Box>
                                                        ))
                                                    }
                                                </Box>
                                            }

                                            {
                                                !personasCargando && personas.length === 0 &&
                                                <Typography variant='body2' className='my-0' sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
                                                    {Lang('SIN_RESULTADOS')}
                                                </Typography>
                                            }

                                            {
                                                !personasCargando && personas.map((p: any) => (
                                                    <Box
                                                        key={p.id}
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                                                    >
                                                        {
                                                            // Ya inscritos: check deshabilitado (no se pueden desmarcar)
                                                            esInscrito(p) ?
                                                            <Checkbox size="small" checked disabled /> :
                                                            (
                                                                p.estado === 'autorizada' &&
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={checkedPersonas.has(p.id)}
                                                                    onChange={() => onTogglePersona(p)}
                                                                />
                                                            )
                                                        }
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant='body2' className='fw-500 my-0' sx={{ wordBreak: 'break-word' }}>
                                                                {p.nombre}
                                                            </Typography>
                                                            <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary' }}>
                                                                {p.nif}
                                                            </Typography>
                                                        </Box>
                                                        {
                                                            p.estado === 'autorizada' ?
                                                                <Chip
                                                                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                                                                    label={Lang('PERSONA_AUTORIZADA')}
                                                                    color="success"
                                                                    size="small"
                                                                /> :
                                                                p.estado === 'pendiente' ?
                                                                    <Chip label={Lang('PENDIENTE')} color="warning" size="small" /> :
                                                                    p.estado === 'rechazada' ?
                                                                        <Chip label={Lang('PERSONA_RECHAZADA')} color="error" size="small" /> :
                                                                        null
                                                        }
                                                    </Box>
                                                ))
                                            }

                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, flexWrap: 'wrap', gap: 1.5 }}>
                                                <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary' }}>
                                                    {personasPaginacion.page} de {personasPaginacion.last_page}
                                                </Typography>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    {
                                                        personasPaginacion.last_page > 1 &&
                                                        <Pagination
                                                            page={personasPaginacion.page}
                                                            count={personasPaginacion.last_page}
                                                            onChange={onPersonasPageChange}
                                                            color="primary"
                                                            size="small"
                                                        />
                                                    }

                                                    <Button
                                                        color="primary"
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<FontAwesomeIcon icon={faUserPlus} />}
                                                        disabled={nuevasMarcadas.length === 0 || inscribiendo}
                                                        onClick={onInscribir}
                                                    >
                                                        {Lang('INSCRIBIR')} ({nuevasMarcadas.length}) {Lang('PERSONAS')}
                                                    </Button>
                                                </Box>
                                            </Box>

                                        </Box>
                                    </Paper>
                                </Box>
                            }

                        </Box>
                    }

                </Paper>

            </Fade>
        </Modal>
    )
}

export default ModalActividad